import { useEffect, useState, useRef } from "react";
import { Coins, Wallet, ArrowDownToLine, Check, Clock, X, Loader2, QrCode, Copy, ArrowUpFromLine, ImagePlus, ShieldCheck, History as HistoryIcon, Ticket } from "lucide-react";
import { Link } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { formatCoins, avatarUrl } from "../lib/format";
import { playReward, playClick } from "../lib/sound";
import Confetti from "../components/Confetti";
import CoinRain from "../components/CoinRain";
import FirstDepositBanner from "../components/FirstDepositBanner";
import FestivalBanner from "../components/FestivalBanner";

const statusStyle = {
  paid: { icon: Check, color: "#00E676" },
  approved: { icon: Check, color: "#00E676" },
  pending: { icon: Clock, color: "#FFD700" },
  rejected: { icon: X, color: "#FF3366" },
};

const QUICK = [100, 200, 500, 1000, 2000, 5000];

export default function Payments() {
  const { user, setCoins, refresh } = useAuth();
  const { t } = useLang();
  const [history, setHistory] = useState([]);
  const [info, setInfo] = useState({ upi_id: "", qr_url: "", note: "", min_withdraw: 200 });
  const [fx, setFx] = useState(0);
  // deposit
  const [depAmount, setDepAmount] = useState(500);
  const [utr, setUtr] = useState("");
  const [depBusy, setDepBusy] = useState(false);
  const [proofUrl, setProofUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const proofRef = useRef(null);
  // withdraw
  const [wd, setWd] = useState({ coins: 200, method: "UPI", account: "" });
  const [wdBusy, setWdBusy] = useState(false);
  const [saveDetails, setSaveDetails] = useState(true);
  const [payout, setPayout] = useState({ upi: "", bank: "" });
  const [minW, setMinW] = useState(200);
  const [kyc, setKyc] = useState({ status: "none", docs: {}, required: ["aadhaar", "pan", "selfie", "bank"] });
  const [promo, setPromo] = useState("");
  const [promoBusy, setPromoBusy] = useState(false);

  const load = () => {
    api.get("/payments/history").then(({ data }) => setHistory(data)).catch(() => {});
    api.get("/payments/info").then(({ data }) => {
      setInfo(data);
      setMinW(data.min_withdraw);
      setWd((w) => ({ ...w, coins: Math.max(w.coins, data.min_withdraw) }));
    }).catch(() => {});
    api.get("/payments/payout").then(({ data }) => {
      setPayout(data);
      setWd((w) => ({ ...w, account: w.method === "UPI" ? (data.upi || "") : (data.bank || "") }));
    }).catch(() => {});
    api.get("/payments/kyc").then(({ data }) => setKyc(data)).catch(() => {});
  };
  useEffect(() => { refresh(); load(); }, []);

  const copyUpi = async () => {
    playClick();
    try { await navigator.clipboard.writeText(info.upi_id); toast.success(t("copied")); } catch { toast(info.upi_id); }
  };

  const uploadProof = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/payments/proof", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setProofUrl(data.url);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const submitDeposit = async (e) => {
    e.preventDefault();
    if (!depAmount || depAmount <= 0) return toast.error(t("amountToAdd"));
    setDepBusy(true);
    playClick();
    try {
      await api.post("/payments/buy", { amount: Number(depAmount), reference: utr, screenshot_url: proofUrl });
      setFx(Date.now());
      playReward();
      toast.success(t("depositRequested"));
      setUtr("");
      setProofUrl("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setDepBusy(false);
    }
  };

  const upiLink = info.upi_id
    ? `upi://pay?pa=${encodeURIComponent(info.upi_id)}&pn=BamBamBhole&cu=INR${depAmount > 0 ? `&am=${depAmount}` : ""}`
    : "";

  const redeemPromo = async () => {
    if (!promo.trim()) return;
    setPromoBusy(true);
    try {
      const { data } = await api.post("/payments/promos/redeem", { code: promo.trim() });
      setCoins(data.coins);
      setFx(Date.now());
      playReward();
      toast.success(`${t("promoApplied")} +${formatCoins(data.amount)}`);
      setPromo("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setPromoBusy(false);
    }
  };

  const pickMethod = (m) => {
    setWd({ ...wd, method: m, account: m === "UPI" ? (payout.upi || "") : (payout.bank || "") });
  };

  const submitWithdraw = async (e) => {
    e.preventDefault();
    setWdBusy(true);
    try {
      const { data } = await api.post("/payments/withdraw", wd);
      setCoins(data.coins);
      if (saveDetails) {
        const next = wd.method === "UPI" ? { ...payout, upi: wd.account } : { ...payout, bank: wd.account };
        api.post("/payments/payout", next).then(() => setPayout(next)).catch(() => {});
      }
      toast.success(t("requestSent"));
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setWdBusy(false);
    }
  };

  return (
    <div className="space-y-8">
      <Confetti fire={fx} />
      <CoinRain fire={fx} />

      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold gold-text">{t("payments")}</h1>
        <div className="mt-2 flex items-center gap-3">
          <div className="inline-flex items-center gap-2 rounded-full border border-amber-500/40 bg-black/40 px-4 py-1.5">
            <Coins size={16} className="text-amber-400" />
            <span className="font-mono-num font-bold text-amber-200">{formatCoins(user?.coins)}</span>
          </div>
          <Link to="/app/history" data-testid="my-history-link" className="inline-flex items-center gap-1.5 rounded-full border border-cyan-400/40 bg-cyan-500/10 px-3 py-1.5 text-xs font-bold text-cyan-300 hover:border-cyan-400 transition-colors">
            <HistoryIcon size={14} /> {t("myHistory")}
          </Link>
        </div>
      </div>

      <div className="mw-card p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="promo-card">
        <div className="flex items-center gap-2 text-sm font-bold text-fuchsia-200 flex-1">
          <Ticket size={18} /> {t("havePromo")}
        </div>
        <input value={promo} onChange={(e) => setPromo(e.target.value.toUpperCase())} placeholder={t("promoCode")}
          data-testid="promo-input" className="flex-1 rounded-lg border border-fuchsia-400/30 bg-black/40 px-3 py-2 text-sm font-mono-num tracking-wider focus:outline-none focus:border-fuchsia-400/80" />
        <button onClick={redeemPromo} disabled={promoBusy} data-testid="promo-redeem-btn"
          className="rounded-lg border border-fuchsia-400/50 bg-fuchsia-500/15 px-5 py-2 text-sm font-bold text-fuchsia-200 flex items-center justify-center gap-2">
          {promoBusy ? <Loader2 size={15} className="animate-spin" /> : <Ticket size={15} />} {t("redeem")}
        </button>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 items-start">
        {/* Deposit */}
        <section className="mw-card p-6" data-testid="deposit-section">
          <h2 className="font-display text-2xl font-bold mb-1 flex items-center gap-2"><ArrowDownToLine size={22} className="text-emerald-400" /> {t("addMoney")}</h2>
          <div className="mt-3 space-y-2"><FestivalBanner compact /><FirstDepositBanner /></div>

          {/* How to */}
          <ol className="mt-3 mb-4 space-y-1 text-xs text-slate-400 list-decimal list-inside">
            <li>{t("depositStep1")}</li>
            <li>{t("depositStep2")}</li>
            <li>{t("depositStep3")}</li>
          </ol>

          {/* UPI / QR */}
          {(info.upi_id || info.qr_url) ? (
            <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4 mb-4 flex flex-col sm:flex-row items-center gap-4" data-testid="pay-upi-block">
              {(info.qr_url || info.upi_id) && (
                <div className="shrink-0 text-center">
                  {info.qr_url ? (
                    <img src={avatarUrl(info.qr_url)} alt="QR" className="h-36 w-36 rounded-lg bg-white p-1 object-contain" data-testid="pay-qr" />
                  ) : (
                    <div className="bg-white p-2 rounded-lg inline-block" data-testid="pay-qr-auto">
                      <QRCodeCanvas value={upiLink} size={132} />
                    </div>
                  )}
                  <p className="text-[10px] text-slate-400 mt-1">{t("scanToPay")}</p>
                </div>
              )}
              {info.upi_id && (
                <div className="min-w-0 flex-1 text-center sm:text-left">
                  <p className="text-[10px] uppercase tracking-wider text-slate-400">{t("payUpi")}</p>
                  <p className="font-mono-num text-lg font-bold text-emerald-300 break-all" data-testid="pay-upi-id">{info.upi_id}</p>
                  <button onClick={copyUpi} data-testid="copy-upi-btn" className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-emerald-500/40 bg-black/30 px-3 py-1.5 text-xs font-bold text-emerald-300">
                    <Copy size={13} /> {t("copyUpi")}
                  </button>
                  {info.note && <p className="text-[11px] text-slate-400 mt-2">{info.note}</p>}
                </div>
              )}
            </div>
          ) : (
            <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 mb-4 flex items-center gap-2 text-sm text-amber-200" data-testid="pay-upi-missing">
              <QrCode size={18} /> {t("depositHint")}
            </div>
          )}

          <form onSubmit={submitDeposit} className="space-y-3">
            <div>
              <label className="text-xs text-slate-400">{t("amountToAdd")}</label>
              <input type="number" min={1} value={depAmount} onChange={(e) => setDepAmount(Number(e.target.value))}
                data-testid="deposit-amount" className="w-full mt-1 rounded-lg border border-amber-500/30 bg-black/40 px-3 py-2 font-mono-num text-amber-200 focus:outline-none focus:border-amber-500/80" />
              <div className="mt-2 flex flex-wrap gap-2" data-testid="deposit-quick">
                {QUICK.map((q) => (
                  <button type="button" key={q} onClick={() => setDepAmount(q)} data-testid={`deposit-quick-${q}`}
                    className={`rounded-full px-3 py-1 text-xs font-bold border transition-colors ${depAmount === q ? "btn-gold" : "border-amber-500/30 bg-black/30 text-amber-200"}`}>₹{q}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-400">{t("enterUtr")}</label>
              <input value={utr} onChange={(e) => setUtr(e.target.value)} placeholder="e.g. 4012XXXXXX89"
                data-testid="deposit-utr" className="w-full mt-1 rounded-lg border border-amber-500/30 bg-black/40 px-3 py-2 text-sm focus:outline-none focus:border-amber-500/80" />
              <p className="text-[11px] text-slate-500 mt-1">{t("utrHint")}</p>
            </div>
            <div>
              <input ref={proofRef} type="file" accept="image/*" onChange={uploadProof} className="hidden" data-testid="deposit-proof-input" />
              {proofUrl ? (
                <div className="flex items-center gap-2" data-testid="deposit-proof-preview">
                  <img src={avatarUrl(proofUrl)} alt="proof" className="h-14 w-14 rounded-lg object-cover border border-emerald-500/40" />
                  <button type="button" onClick={() => setProofUrl("")} className="text-xs font-bold text-rose-400 flex items-center gap-1"><X size={13} /> remove</button>
                </div>
              ) : (
                <button type="button" onClick={() => proofRef.current?.click()} disabled={uploading} data-testid="deposit-proof-btn"
                  className="flex items-center gap-2 rounded-lg border border-emerald-500/40 bg-black/30 px-3 py-2 text-xs font-bold text-emerald-300">
                  {uploading ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />} {t("uploadProof")}
                </button>
              )}
            </div>
            <button type="submit" disabled={depBusy} data-testid="deposit-submit" className="w-full btn-gold rounded-lg py-2.5 flex items-center justify-center gap-2">
              {depBusy ? <Loader2 size={16} className="animate-spin" /> : <ArrowDownToLine size={16} />} {t("submitDeposit")}
            </button>
          </form>
        </section>

        {/* Withdraw */}
        <section className="mw-card p-6" data-testid="withdraw-section">
          <h2 className="font-display text-2xl font-bold mb-1 flex items-center gap-2"><ArrowUpFromLine size={22} className="text-cyan-400" /> {t("withdraw")}</h2>
          <p className="text-xs text-slate-400 mb-4">{t("minWithdrawNote")} (Min {formatCoins(minW)})</p>
          <KycSection kyc={kyc} reload={load} />
          <form onSubmit={submitWithdraw} className="space-y-3">
            <div>
              <label className="text-xs text-slate-400">{t("amountCoins")} (min {formatCoins(minW)})</label>
              <input type="number" min={minW} value={wd.coins} onChange={(e) => setWd({ ...wd, coins: Number(e.target.value) })}
                data-testid="withdraw-coins" className="w-full mt-1 rounded-lg border border-amber-500/30 bg-black/40 px-3 py-2 font-mono-num text-amber-200 focus:outline-none focus:border-amber-500/80" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              {["UPI", "Bank"].map((m) => (
                <button type="button" key={m} onClick={() => pickMethod(m)}
                  data-testid={`withdraw-method-${m}`}
                  className={`rounded-lg py-2 text-sm font-bold border transition-colors ${wd.method === m ? "btn-gold" : "border-amber-500/30 bg-black/30 text-amber-200"}`}>
                  {m === "UPI" ? t("upiId") : t("bankAccount")}
                </button>
              ))}
            </div>
            <input value={wd.account} onChange={(e) => setWd({ ...wd, account: e.target.value })} required
              placeholder={wd.method === "UPI" ? "name@upi" : "A/C number"}
              data-testid="withdraw-account"
              className="w-full rounded-lg border border-amber-500/30 bg-black/40 px-3 py-2 text-sm focus:outline-none focus:border-amber-500/80" />
            <label className="flex items-center gap-2 text-xs text-slate-400 cursor-pointer" data-testid="withdraw-save-toggle">
              <input type="checkbox" checked={saveDetails} onChange={(e) => setSaveDetails(e.target.checked)} className="accent-amber-500" />
              {t("saveDetails")}
            </label>
            <button type="submit" disabled={wdBusy || kyc.status !== "approved"} data-testid="withdraw-submit" className="w-full btn-gold rounded-lg py-2.5 flex items-center justify-center gap-2 disabled:opacity-50">
              {wdBusy && <Loader2 size={16} className="animate-spin" />} {t("withdraw")}
            </button>
          </form>
        </section>
      </div>

      {/* History */}
      <section className="mw-card p-6">
        <h2 className="font-display text-2xl font-bold mb-4 flex items-center gap-2"><Wallet size={22} className="text-amber-400" /> {t("paymentHistory")}</h2>
        <div className="space-y-2 max-h-[360px] overflow-y-auto" data-testid="payment-history">
          {history.length === 0 && <p className="text-slate-500 text-sm">—</p>}
          {history.map((h, i) => {
            const st = statusStyle[h.status] || statusStyle.pending;
            const StIcon = st.icon;
            const isDep = h.type === "deposit";
            return (
              <div key={h.id || i} className="flex items-center justify-between rounded-lg border border-white/5 bg-black/20 px-3 py-2">
                <div className="flex items-center gap-2">
                  <StIcon size={16} style={{ color: st.color }} />
                  <div>
                    <p className="text-sm font-semibold">{isDep ? t("deposit") : t("withdrawal")}{h.method && h.method !== "demo" && h.method !== "manual" ? ` · ${h.method}` : ""}{h.reference ? ` · ${h.reference}` : ""}</p>
                    <p className="text-[11px] capitalize" style={{ color: st.color }}>{t(h.status) || h.status}</p>
                  </div>
                </div>
                <span className={`font-mono-num font-bold text-sm ${isDep ? "text-emerald-400" : "text-amber-300"}`}>
                  {isDep ? "+" : "-"}{formatCoins(h.coins)}
                </span>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}

const DOC_META = [
  { k: "aadhaar", labelKey: "docAadhaar" },
  { k: "pan", labelKey: "docPan" },
  { k: "selfie", labelKey: "docSelfie" },
  { k: "bank", labelKey: "docBank" },
];

function KycSection({ kyc, reload }) {
  const { t } = useLang();
  const [busy, setBusy] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const refs = useRef({});

  if (kyc.status === "approved") {
    return (
      <div className="mb-4 flex items-center gap-2 rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-300" data-testid="kyc-approved-badge">
        <ShieldCheck size={18} /> {t("kycApproved")}
      </div>
    );
  }

  const upload = async (k, file) => {
    if (!file) return;
    setBusy(k);
    const fd = new FormData();
    fd.append("doc_type", k);
    fd.append("file", file);
    try { await api.post("/payments/kyc/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); reload(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const submit = async () => {
    setSubmitting(true);
    try { await api.post("/payments/kyc/submit"); toast.success(t("kycPending")); reload(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  const allUploaded = DOC_META.every((d) => kyc.docs?.[d.k]);

  return (
    <div className="mb-4 rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 space-y-3" data-testid="kyc-section">
      <div className="flex items-center gap-2 text-sm font-bold text-amber-200">
        <ShieldCheck size={18} /> {t("kycVerify")}
      </div>
      <p className={`text-xs ${kyc.status === "pending" ? "text-amber-300" : kyc.status === "rejected" ? "text-rose-300" : "text-slate-400"}`}>
        {kyc.status === "pending" ? t("kycPending") : kyc.status === "rejected" ? t("kycRejected") : t("kycRequired")}
      </p>
      {kyc.status !== "pending" && (
        <>
          <div className="grid grid-cols-2 gap-2">
            {DOC_META.map((d) => {
              const done = kyc.docs?.[d.k];
              return (
                <div key={d.k}>
                  <input ref={(el) => (refs.current[d.k] = el)} type="file" accept="image/*" className="hidden"
                    data-testid={`kyc-upload-input-${d.k}`} onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; upload(d.k, f); }} />
                  <button type="button" onClick={() => refs.current[d.k]?.click()} disabled={busy === d.k}
                    data-testid={`kyc-upload-${d.k}`}
                    className={`w-full flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-xs font-bold ${done ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-300" : "border-amber-500/40 bg-black/30 text-amber-200"}`}>
                    {busy === d.k ? <Loader2 size={13} className="animate-spin" /> : done ? <Check size={13} /> : <ImagePlus size={13} />}
                    {t(d.labelKey)}{done ? ` · ${t("uploaded")}` : ""}
                  </button>
                </div>
              );
            })}
          </div>
          <button type="button" onClick={submit} disabled={!allUploaded || submitting} data-testid="kyc-submit"
            className="w-full btn-gold rounded-lg py-2 text-sm flex items-center justify-center gap-2 disabled:opacity-50">
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />} {t("submitKyc")}
          </button>
        </>
      )}
    </div>
  );
}
